import React, { useEffect } from 'react';
import { Box, Button, TextInput, useToast } from '@razorpay/blade/components';
import { SelectState } from 'components/OfficeLocation/CreateOfficeLocations/components/SelectState';
import { SelectDistrict } from 'components/OfficeLocation/CreateOfficeLocations/components/SelectDistrict';
import { SelectDispensary } from 'components/OfficeLocation/CreateOfficeLocations/components/SelectDispensary';
import { useFormWithProvider } from 'forms';
import { OfficeLocationFormData } from 'components/OfficeLocation/CreateOfficeLocations/types';
import { routePaths } from 'components/Routes/data';
import api from 'api';
import { useMutation } from 'react-query';
import { validationSchema } from 'components/OfficeLocation/CreateOfficeLocations/utils';
import { FormContainer } from 'components/Onboarding/CompanyDetails/styles';
import { AppError } from 'utils/AppError';
import { parseError } from 'utils/Api';

export const OfficeLocationForm = () => {
  const { register, formData, validate, setFieldValue } = useFormWithProvider<
    OfficeLocationFormData & { id: number }
  >();
  const toast = useToast();

  const updateOfficeLocationMutation = useMutation({
    mutationFn: api.officeLocation.updateOfficeLocation,
    onSuccess: () => {
      window.location.href = routePaths.phpRoutes.dashboard;
    },
    onError: (e: typeof AppError) => {
      toast.show({
        content: parseError(e),
        color: 'negative',
      });
    },
  });

  const handleUpdateOfficeLocation = () => {
    const isValid = validate(validationSchema);
    if (isValid && formData.id) {
      updateOfficeLocationMutation.mutate({
        officeId: formData.id,
        data: {
          office_name: formData.officeName!,
          address_line_1: formData.officeAddress!,
          address_line_2: '',
          district: formData.district?.[0] || '',
          state: Number(formData.state?.[0]),
          pincode: formData.pincode || '',
          esic_dispensary: formData.esicDispensary?.[0] || null,
          esic_sub_code: formData.esicSubCode ? Number(formData.esicSubCode) : null,
        },
      });
    }
  };

  const selectedDistrict = formData?.district?.[0];

  useEffect(() => {
    setFieldValue('esicDispensary', []);
  }, [selectedDistrict]);

  return (
    <FormContainer>
      <TextInput
        marginTop="spacing.7"
        label="Office Name"
        necessityIndicator="required"
        placeholder="Enter office name"
        size="medium"
        {...register('officeName')}
      />

      <TextInput
        label="Office Address"
        necessityIndicator="required"
        placeholder="Enter office address"
        size="medium"
        {...register('officeAddress')}
        isDisabled
      />

      <Box display="flex" flexDirection="row" gap="spacing.5" flex="1">
        <SelectState
          isDisabled={true}
          formData={formData}
          register={({ name, type }) => {
            return register(name, type);
          }}
        />
        <SelectDistrict
          formData={formData}
          register={({ name, type }) => {
            return register(name, type);
          }}
        />
      </Box>

      <TextInput
        label="Pincode"
        necessityIndicator="required"
        placeholder="Enter Pincode"
        size="medium"
        {...register('pincode')}
        isDisabled
      />

      <SelectDispensary
        formData={formData}
        register={({ name, type }) => {
          return register(name, type);
        }}
      />

      <TextInput
        label="ESIC Sub-Code"
        necessityIndicator="none"
        helpText="Location-specific sub-code assigned by the government"
        placeholder="Enter ESIC Sub-Code"
        size="medium"
        {...register('esicSubCode')}
      />

      <Box marginY="spacing.7" display="flex" flexDirection="column" gap="spacing.5">
        <Box>
          <Button
            isLoading={updateOfficeLocationMutation.isLoading}
            onClick={handleUpdateOfficeLocation}>
            Confirm & Proceed
          </Button>
        </Box>
      </Box>
    </FormContainer>
  );
};
